import { useEffect, useLayoutEffect, useRef } from 'react';
import { masterKeyIntent, stepCursor } from '../utils/masterKeyboard';

type KeyEventLike = Parameters<typeof masterKeyIntent>[0];

/** The DOM id of a line's table row, for scrolling it into view. */
export function lineRowDomId(suggestionId: string): string {
  return `buyer-board-line-${suggestionId}`;
}

/**
 * The line a keydown moves the selection to: ↑/↓ (or j/k) step through the
 * lines in the order the table shows them, and stop at both ends. Null when
 * the key is not a step, no line is selected, or the selected line is not
 * shown (filtered out): the event is then left to the page.
 */
export function lineStepTarget(
  event: KeyEventLike,
  visibleLineIds: readonly string[],
  selectedLineId: string | undefined,
): string | null {
  if (selectedLineId === undefined) return null;
  const intent = masterKeyIntent(event);
  if (intent !== 'next' && intent !== 'prev') return null;
  if (!visibleLineIds.includes(selectedLineId)) return null;
  return stepCursor(visibleLineIds, selectedLineId, intent);
}

/**
 * While a line is selected, ↑/↓ move the selection to the next or previous
 * line, so the Line section follows a buyer working down the cart (GEN-2944:
 * "boom, boom, boom" with the eyes on the bottom pane).
 */
export function useLineStepKeys(
  visibleLineIds: readonly string[],
  selectedLineId: string | undefined,
  onSelectLine: (suggestionId: string) => void,
  disabled = false,
): void {
  // Read through a ref so a caller's fresh callback each render doesn't
  // resubscribe the listener.
  const onSelectLineRef = useRef(onSelectLine);
  useLayoutEffect(() => {
    onSelectLineRef.current = onSelectLine;
  });
  useEffect(() => {
    if (disabled || selectedLineId === undefined) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const target = lineStepTarget(event, visibleLineIds, selectedLineId);
      if (target === null) return;
      // At either end the key is still ours: no page scroll, no order cursor.
      // The callback still runs with the same line, so a caller can collapse
      // a multi-selection onto it.
      event.preventDefault();
      onSelectLineRef.current(target);
      document
        .getElementById(lineRowDomId(target))
        ?.scrollIntoView({ block: 'nearest' });
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [disabled, visibleLineIds, selectedLineId]);
}
